import  React from 'react'
import * as H from 'history'
import { Alert } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import {
  Checkbox, FormItem, validateFields, TextField, ShowIf,
} from '../../shared/fields'
import { NextBackButtons } from './controls/NextBackButtons'
import { ClaimModel } from '../../models/ClaimModel'
import { ClaimController } from '../../controllers/ClaimController'
import { Backdrop } from '../../shared/Backdrop'
import { ISupportSpinner } from '../../shared/Ajax'
import { getFY, LodgementPeriodType, ConsultantType } from '../../shared/Utility'
import { LodgementPeriod } from '../../models/LodgementPeriod'

export interface TermsPageProps {
    history: H.History
    model: ClaimModel
    onChange: { (changes: Partial<ClaimModel>): any }
    preview?: boolean
}

export interface TermsPageState {
    loading: boolean
    lodgementPeriods: LodgementPeriod[]
}

export class TermsPage extends React.Component<TermsPageProps, TermsPageState> implements ISupportSpinner {
    terms: Checkbox
    privacy: Checkbox
    shareInformation: Checkbox
    declaration: Checkbox
    ajax: ClaimController

    constructor(props: TermsPageProps) {
      super(props)
      this.ajax = new ClaimController()
      this.state = {
        loading: false,
        lodgementPeriods: [],
      }
    }

    componentDidMount() {
      this.ajax.getLodgementPeriods(null, this.props.model.claimYear, LodgementPeriodType.Lodgement, (d) => {
        this.setState({ lodgementPeriods: d })
      })
    }

    componentWillUnmount() {
      this.ajax.cancel()
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
      this.setState({ loading: on }, callback)
    }

    next = () => {
      validateFields((res) => {
        if (res) {
          this.ajax.updateClaim(this, this.props.model, () => {
            this.props.history.push(`/claim/${this.props.model.claimId}/business`)
          })
        }
      },
      this.terms,
      this.privacy,
      this.shareInformation,
      this.declaration)
    }

    render() {
      let nonQIPLodgementPeriodString = ''
      const nonQIPPeriods = this.state.lodgementPeriods.filter((p) => p.claimantType == (this.props.model.isConsultant ? ConsultantType.Consultant : ConsultantType.Individial))
      if (nonQIPPeriods.length > 0) { nonQIPLodgementPeriodString = `${nonQIPPeriods[0].startDateString} and ${nonQIPPeriods[0].endDateString}` }

      let qipLodgementPeriodString = ''
      const qipPeriods = this.state.lodgementPeriods.filter((p) => p.claimantType == ConsultantType.QIPConsultant)
      if (qipPeriods.length > 0) { qipLodgementPeriodString = `${qipPeriods[0].endDateString}` }

      return (
        <>
          <ShowIf condition={this.props.preview != true}>
            <Alert className="emdg-page-alerts">
              Austrade must receive your completed application and supporting documents between
              {' '}
              {nonQIPLodgementPeriodString}
              .
              <a href="https://www.austrade.gov.au/Australian/Export/Export-Grants/Assistance/Consultants/consultants" target="_blank" rel="noreferrer">Approved EMDG consultants</a>
              {' '}
              can lodge applications until
              {qipLodgementPeriodString}
              .
              <br />
              Eligible expenses added to your claim after you have submitted it, and before the grant is determined, will be limited to 10% of total eligible expenses.
              <br />
              <b>
                You MUST click the SUBMIT button on the Acknowledgement and Submit page to lodge your application. Your application will NOT be considered received or valid by Austrade until you click this button. When submitted, you will receive a confirmation on screen and by email from us for your records. If you do not receive a confirmation after submission, you please email us on
                <a href="mailto:emdg.help@austrade.gov.au">emdg.help@austrade.gov.au</a>
                .
              </b>
            </Alert>
          </ShowIf>
          <h4>TERMS AND CONDITIONS</h4>
          <div className="form-item">
            <Checkbox
              ref={(r) => this.terms = r}
              id="termsAndConditions"
              required
              requiredMessage="You must accept terms & conditions"
              onChange={(terms) => this.props.onChange({ terms })}
              value={this.props.model.terms}
              label={(
                <span>
                  I have read and accept the
                  <a href="https://www.austrade.gov.au/ArticleDocuments/1433/EMDG-Terms-and-conditions-of-use.pdf.aspx" target="_blank" rel="noreferrer">Terms and Conditions of Use</a>
                  .
                </span>
)}
              preview={this.props.preview}
            />
          </div>

          <h4>CONFIDENTIALITY AND PRIVACY</h4>
          <div className="form-item">
            <Checkbox
              ref={(r) => this.privacy = r}
              id="privacy"
              required
              requiredMessage="You must accept confidentiality & privacy"
              onChange={(privacy) => this.props.onChange({ privacy })}
              value={this.props.model.privacy}
              label={(
                <span>
                  I have read and accept the
                  <a href="https://www.austrade.gov.au/ArticleDocuments/1433/EMDG-Confidentiality-and-Privacy.pdf.aspx" target="_blank" rel="noreferrer">Confidentiality and Privacy</a>
                  {' '}
                  provisions
                </span>
)}
              preview={this.props.preview}
            />
          </div>

          <h4 style={{ display: this.props.model.isConsultant ? 'none' : '' }}>APPLICANT DECLARATION REQUIREMENTS</h4>
          <div style={{ display: this.props.model.isConsultant ? 'none' : '' }}>
            <p>Before you submit your application you will be required to complete, sign, scan and attach an Applicant Declaration document. The declaration will require you to declare that:</p>
            <ul>
              <li>all information supplied in this application form, its schedules and any accompanying documents is complete and accurate, and is not false or misleading, as at the date it is submitted to Austrade;</li>
              <li>all information in the application and its schedules is capable of substantiation for the amount and purpose of expenses;</li>
              <li>I have made all necessary enquiries to satisfy myself that the expenses I am claiming in the following application meet the eligibility requirements; and</li>
              <li>all documents that may be relevant to this application will be made available (at my expense) to Austrade upon request.</li>
            </ul>
            <p>
              I understand that eligible expenses disclosed to Austrade after lodgement of the application and before determination will be excluded if such expenses exceed 10 per cent of the amount of eligible expenses disclosed in the application, the completed schedules and all other required documents as per
              <a href="http://www.austrade.gov.au/ArticleDocuments/1433/EMDG-A-Guide-Applying-EMDG.pdf.aspx" target="_blank" rel="noreferrer">A Guide to Applying for EMDG</a>
              .
            </p>
            <div className="form-item">
              <Checkbox
                ref={(r) => this.declaration = r}
                id="declaration"
                required={!this.props.model.isConsultant}
                requiredMessage="You must accept applicant declaration requirements"
                onChange={(declaration) => this.props.onChange({ declaration })}
                value={this.props.model.declaration}
                label="I have read and accept the Applicant Declaration requirements stated above."
                preview={this.props.preview}
              />
            </div>
          </div>

          <h4>ADDITIONAL INFORMATION SHARING</h4>
          <div className="form-item">
            <Checkbox
              ref={(r) => this.shareInformation = r}
              id="shareInformation"
              required={false}

              onChange={(shareInformation) => this.props.onChange({ shareInformation })}
              value={this.props.model.shareInformation}
              label={<span>I agree that my contact details may be shared with other government bodies for trade facilitation or business support purposes. E.g. Export Finance Corporation.</span>}
              preview={this.props.preview}
            />
          </div>

          <NextBackButtons onNext={this.next} onBack={this.props.history.goBack} preview={this.props.preview} />
          <Backdrop on={this.state.loading} />
        </>
      )
    }
}
